import React from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { Theme } from "@pga/pga-component-library"
import { Button } from "../Button"
import { formatDate } from "../utils/formatter"
import {
  mapPtmApplicationStatus,
  VIEW_APPLICATION_LABEL,
} from "../../utils/prospect/ptmApplicationStatus"
import {
  REINSTATEMENT_SECTION_EMPLOYMENT_VERIFICATION_MESSAGE,
  SECTION_EMPLOYMENT_VERIFICATION_LABEL,
  showsSectionEmploymentVerification,
} from "../../utils/sectionEmploymentVerification"
import {
  blueGrey,
  darkgrey,
  gold,
  lightBlue,
  primary,
  textGrey,
  white,
} from "../../assets/colors"
import {
  BACKGROUND_CHECK_STEP_STATE,
  PDR_STEP_STATE,
  resolveBackgroundCheckStep,
  resolvePdrStep,
} from "./reinstatementStepStatus"

export const REINSTATEMENT_STEPS_TITLE = "Steps to Reinstatement"
export const BACKGROUND_CHECK_PAGE_PATH = "/background-check"
export const BACKGROUND_CHECK_TIMING_NOTE =
  "A background check is only valid for one year. Submit yours when you are close to completing your PDR requirement so it is still current when you apply."

export const PDR_STEP_MET_COPY =
  "You have met the PDR requirement for reinstatement."
export const PDR_STEP_NOT_MET_COPY =
  "Earn the remaining PDRs below to meet the PDR requirement for reinstatement."
export const PDR_STEP_CONTACT_COPY =
  "Contact Membership Services to get the latest PDR requirement in order to Reinstate."

export const PDR_STEP_LOADING_COPY = "Loading your PDR requirement…"

export const pdrBucketCopy = ({ label, earned, required, remaining }) =>
  remaining > 0
    ? `${label}: ${earned} of ${required} earned, ${remaining} remaining`
    : `${label}: ${earned} of ${required} earned`

// Cap the fill so a bucket earned past its requirement stays a full bar.
export const pdrBarPercent = (earned, required) => {
  if (!(required > 0)) return 0
  return Math.min(
    100,
    Math.round((Math.min(earned, required) / required) * 100),
  )
}

const STEP_STATUS_LABEL = {
  required: "Required",
  complete: "Complete",
  inProgress: "In Progress",
  incomplete: "Not Started",
  expired: "Expired",
  notApproved: "Not Approved",
  locked: "Locked",
  ready: "Ready",
}

const Section = styled.section`
  width: 100%;
  margin-top: 8px;
  text-align: left;
  font-family: ${Theme.fonts.Montserrat};
`

const Heading = styled.h2`
  margin: 0 0 4px;
  font-family: ${Theme.fonts.PlayfairDisplay};
  font-size: 20px;
  font-weight: 600;
  color: ${primary};
`

const Intro = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  color: ${textGrey};
`

const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`

const StepItem = styled.li`
  display: flex;
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid ${darkgrey};
`

const StepNumber = styled.span`
  flex: 0 0 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: ${({ $complete }) => ($complete ? white : primary)};
  background-color: ${({ $complete }) => ($complete ? lightBlue : white)};
  border: 2px solid ${({ $locked }) => ($locked ? darkgrey : lightBlue)};
`

const StepBody = styled.div`
  flex: 1;
  min-width: 0;
`

const StepHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const StepTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $locked }) => ($locked ? blueGrey : primary)};
`

const StepStatus = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${({ $complete }) => ($complete ? lightBlue : textGrey)};
`

const StepText = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  color: ${textGrey};
`

const Note = styled.p`
  margin: 8px 0 0;
  padding: 8px 12px;
  font-size: 13px;
  color: ${primary};
  background-color: #eef6fc;
  border-left: 3px solid ${lightBlue};
`

const BucketList = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
`

const BucketRow = styled.li`
  display: grid;
  grid-template-columns: 8rem 14rem auto;
  align-items: center;
  column-gap: 12px;
  width: max-content;
  max-width: 100%;
  margin-top: 8px;
  font-size: 14px;
  color: ${primary};

  @media (max-width: 575.98px) {
    grid-template-columns: minmax(0, 8rem) minmax(0, 1fr) auto;
    width: 100%;
    min-width: 0;
  }
`

const BucketLabel = styled.span`
  min-width: 0;
  overflow-wrap: break-word;
`

const BucketTrack = styled.div`
  width: 100%;
  min-width: 0;
  height: 8px;
  border-radius: 4px;
  background: ${({ $met }) =>
    $met ? "rgba(22, 177, 105, 0.22)" : "rgba(180, 151, 90, 0.35)"};
  overflow: hidden;
`

const BucketFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: ${({ $met }) => ($met ? Theme.colors.successGreen : gold)};
`

const BucketCount = styled.span`
  flex: 0 0 auto;
  white-space: nowrap;
`

const StepLink = styled.a`
  display: inline-block;
  margin-top: 8px;
  font-size: 14px;
`

const StepRouterLink = styled(Link)`
  display: inline-block;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
`

export const SUBMIT_READY_COPY =
  "You are ready to submit your reinstatement application."
export const SUBMIT_PDR_OPEN_COPY =
  "You can submit your reinstatement application now. Membership Services confirms your PDR requirement when they review it."
export const SUBMIT_LOCKED_COPY =
  "Available once your background check is approved."

const formatLongDate = date => (date ? formatDate(date, "MMMM D, YYYY") : null)

const Step = ({ number, title, status, complete, locked, children }) => (
  <StepItem>
    <StepNumber $complete={complete} $locked={locked} aria-hidden="true">
      {number}
    </StepNumber>
    <StepBody>
      <StepHeader>
        <StepTitle $locked={locked}>
          <span className="sr-only">Step {number}: </span>
          {title}
        </StepTitle>
        <StepStatus $complete={complete}>{status}</StepStatus>
      </StepHeader>
      {children}
    </StepBody>
  </StepItem>
)

const PdrBucket = ({ bucket, index }) => {
  const copy = pdrBucketCopy(bucket)
  const showBar = bucket.required > 0
  const met = bucket.remaining === 0
  const labelId = `reinstatement-pdr-${index}`

  return (
    <BucketRow>
      <BucketLabel id={labelId}>{bucket.label}</BucketLabel>
      {showBar ? (
        <BucketTrack
          $met={met}
          role="progressbar"
          aria-labelledby={labelId}
          aria-valuemin={0}
          aria-valuemax={bucket.required}
          aria-valuenow={Math.min(bucket.earned, bucket.required)}
          aria-valuetext={copy}
        >
          <BucketFill
            $met={met}
            style={{
              width: `${pdrBarPercent(bucket.earned, bucket.required)}%`,
            }}
          />
        </BucketTrack>
      ) : (
        <span className="sr-only">{copy}</span>
      )}
      <BucketCount>
        {bucket.earned} of {bucket.required}
      </BucketCount>
    </BucketRow>
  )
}

const PDR_STEP_COPY = {
  [PDR_STEP_STATE.MET]: PDR_STEP_MET_COPY,
  [PDR_STEP_STATE.NOT_MET]: PDR_STEP_NOT_MET_COPY,
  [PDR_STEP_STATE.UNKNOWN]: PDR_STEP_CONTACT_COPY,
}

// Display only: Submit is gated by the background check, and Membership
// Services confirms PDRs when they review the application.
const PdrStep = ({
  pdrRequirements,
  loading,
  pdrResourcesUrl,
  pdrResourcesCopy,
}) => {
  const pdr = resolvePdrStep(loading ? null : pdrRequirements)

  return (
    <Step
      number={1}
      title="Satisfy PDR Requirement"
      status={
        pdr.complete ? STEP_STATUS_LABEL.complete : STEP_STATUS_LABEL.required
      }
      complete={pdr.complete}
    >
      <StepText aria-busy={loading ? "true" : undefined}>
        {loading ? PDR_STEP_LOADING_COPY : PDR_STEP_COPY[pdr.state]}
      </StepText>
      {pdr.requirements.length > 0 && (
        <BucketList aria-label="Reinstatement PDR requirement">
          {pdr.requirements.map((bucket, index) => (
            <PdrBucket
              key={`${index}-${bucket.label}`}
              bucket={bucket}
              index={index}
            />
          ))}
        </BucketList>
      )}
      {pdrResourcesUrl && (
        <StepLink
          href={pdrResourcesUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {pdrResourcesCopy}
        </StepLink>
      )}
    </Step>
  )
}

const backgroundCheckCopy = (backgroundCheck, submitted) => {
  switch (backgroundCheck.state) {
    case BACKGROUND_CHECK_STEP_STATE.APPROVED: {
      const expiresOn = formatLongDate(backgroundCheck.expiresAt)
      if (!expiresOn) return "Your background check is approved."
      const validUntil = `Your background check is approved and valid until ${expiresOn}.`
      return submitted
        ? validUntil
        : `${validUntil} Submit your application before it expires.`
    }
    case BACKGROUND_CHECK_STEP_STATE.IN_PROGRESS:
      return "Your background check is in progress."
    case BACKGROUND_CHECK_STEP_STATE.DENIED:
      return "Your background check was not approved. Contact PGA Membership Services for next steps."
    case BACKGROUND_CHECK_STEP_STATE.EXPIRED: {
      const expiredOn = formatLongDate(backgroundCheck.expiredAt)
      return expiredOn
        ? `Your last background check expired on ${expiredOn}. Reinstatement requires a new background check.`
        : "Your last background check has expired. Reinstatement requires a new background check."
    }
    default:
      return "Reinstatement requires a new background check."
  }
}

const backgroundCheckLinkLabel = backgroundCheck => {
  switch (backgroundCheck.state) {
    case BACKGROUND_CHECK_STEP_STATE.IN_PROGRESS:
      return backgroundCheck.canContinue ? "Continue Background Check" : null
    case BACKGROUND_CHECK_STEP_STATE.APPROVED:
    case BACKGROUND_CHECK_STEP_STATE.DENIED:
      return null
    default:
      return "Start Background Check"
  }
}

const backgroundCheckStatusLabel = state => {
  switch (state) {
    case BACKGROUND_CHECK_STEP_STATE.APPROVED:
      return STEP_STATUS_LABEL.complete
    case BACKGROUND_CHECK_STEP_STATE.IN_PROGRESS:
      return STEP_STATUS_LABEL.inProgress
    case BACKGROUND_CHECK_STEP_STATE.EXPIRED:
      return STEP_STATUS_LABEL.expired
    case BACKGROUND_CHECK_STEP_STATE.DENIED:
      return STEP_STATUS_LABEL.notApproved
    default:
      return STEP_STATUS_LABEL.incomplete
  }
}

const BackgroundCheckStep = ({ backgroundCheck, submitted }) => {
  const status = backgroundCheckStatusLabel(backgroundCheck.state)
  const linkLabel = backgroundCheckLinkLabel(backgroundCheck)

  return (
    <Step
      number={2}
      title="New Background Check"
      status={status}
      complete={backgroundCheck.complete}
    >
      <StepText>{backgroundCheckCopy(backgroundCheck, submitted)}</StepText>
      {!backgroundCheck.complete && !submitted && (
        <Note>{BACKGROUND_CHECK_TIMING_NOTE}</Note>
      )}
      {linkLabel && (
        <StepRouterLink to={BACKGROUND_CHECK_PAGE_PATH}>
          {linkLabel}
        </StepRouterLink>
      )}
    </Step>
  )
}

const SubmitStep = ({ unlocked, pdrReady }) => (
  <Step
    number={3}
    title="Submit Application"
    status={unlocked ? STEP_STATUS_LABEL.ready : STEP_STATUS_LABEL.locked}
    complete={false}
    locked={!unlocked}
  >
    <StepText id="reinstatement-submit-help">
      {!unlocked
        ? SUBMIT_LOCKED_COPY
        : pdrReady
          ? SUBMIT_READY_COPY
          : SUBMIT_PDR_OPEN_COPY}
    </StepText>
  </Step>
)

// The applicant cannot edit a submitted application, so changes go through
// Membership Services rather than the View Application page.
export const CHANGES_REQUESTED_COPY =
  "Membership Services requested changes to your reinstatement application. Contact them at the number above."
export const CHANGES_REQUESTED_LABEL = "Changes Requested"

const SUBMITTED_APPLICATION_COPY = {
  submitted:
    "Your reinstatement application was submitted. Membership Services will review it.",
  under_review:
    "Membership Services is reviewing your reinstatement application.",
  changes_requested: CHANGES_REQUESTED_COPY,
  approved: "Your reinstatement application was approved.",
}

const SUBMITTED_STATUS_LABEL = {
  changes_requested: CHANGES_REQUESTED_LABEL,
}

export const SUBMITTED_APPLICATION_INTRO =
  "You have submitted your reinstatement application."

const SubmittedStep = ({ application }) => {
  const status = String(application.status).toLowerCase()
  const awaitingSection = showsSectionEmploymentVerification(
    status,
    application.awaitingSectionEmploymentVerification,
  )
  const statusLabel = awaitingSection
    ? SECTION_EMPLOYMENT_VERIFICATION_LABEL
    : SUBMITTED_STATUS_LABEL[status] ||
      mapPtmApplicationStatus(application.status)?.text

  return (
    <Step number={3} title="Submit Application" status={statusLabel} complete>
      <StepText>
        {awaitingSection
          ? REINSTATEMENT_SECTION_EMPLOYMENT_VERIFICATION_MESSAGE
          : SUBMITTED_APPLICATION_COPY[status]}
      </StepText>
    </Step>
  )
}

const resolveSubmitPresentation = ({
  backgroundCheck,
  backgroundCheckOrder,
  pdrRequirements,
  pdrRequirementsLoading,
}) => {
  const bgc = resolveBackgroundCheckStep({
    backgroundCheck,
    order: backgroundCheckOrder,
  })
  const unlocked = bgc.complete
  const pdrForSubmit = resolvePdrStep(
    pdrRequirementsLoading ? null : pdrRequirements,
  )
  const pdrReady =
    !pdrRequirementsLoading && pdrForSubmit.state === PDR_STEP_STATE.MET
  return { bgc, unlocked, pdrReady }
}

// The step explains readiness. The action itself sits in the modal footer,
// beside Return to Public Content.
export const ReinstatementFooterAction = ({
  loading,
  backgroundCheck,
  backgroundCheckOrder,
  applyLabel,
  onApply,
  pdrRequirements,
  pdrRequirementsLoading,
  application,
}) => {
  if (loading) return null

  if (application) {
    return (
      <Button color="primary" outline className="m-2" onClick={onApply}>
        {VIEW_APPLICATION_LABEL}
      </Button>
    )
  }

  const { unlocked, pdrReady } = resolveSubmitPresentation({
    backgroundCheck,
    backgroundCheckOrder,
    pdrRequirements,
    pdrRequirementsLoading,
  })

  return (
    <Button
      color="primary"
      className="m-2"
      outline={unlocked && !pdrReady}
      disabled={!unlocked}
      aria-describedby="reinstatement-submit-help"
      onClick={unlocked ? onApply : undefined}
    >
      {applyLabel}
    </Button>
  )
}

const ReinstatementSteps = ({
  loading,
  backgroundCheck,
  backgroundCheckOrder,
  pdrResourcesUrl,
  pdrResourcesCopy,
  pdrRequirements,
  pdrRequirementsLoading,
  application,
}) => {
  if (loading) {
    return (
      <Section aria-busy="true">
        <Heading>{REINSTATEMENT_STEPS_TITLE}</Heading>
        <Intro>Loading your reinstatement steps…</Intro>
      </Section>
    )
  }

  const { bgc, unlocked, pdrReady } = resolveSubmitPresentation({
    backgroundCheck,
    backgroundCheckOrder,
    pdrRequirements,
    pdrRequirementsLoading,
  })

  return (
    <Section aria-labelledby="reinstatement-steps-heading">
      <Heading id="reinstatement-steps-heading">
        {REINSTATEMENT_STEPS_TITLE}
      </Heading>
      <Intro>
        {application
          ? SUBMITTED_APPLICATION_INTRO
          : "Complete these steps to apply for reinstatement."}
      </Intro>
      <StepList>
        <PdrStep
          pdrRequirements={pdrRequirements}
          loading={pdrRequirementsLoading}
          pdrResourcesUrl={pdrResourcesUrl}
          pdrResourcesCopy={pdrResourcesCopy}
        />
        <BackgroundCheckStep
          backgroundCheck={bgc}
          submitted={Boolean(application)}
        />
        {application ? (
          <SubmittedStep application={application} />
        ) : (
          <SubmitStep unlocked={unlocked} pdrReady={pdrReady} />
        )}
      </StepList>
    </Section>
  )
}

export default ReinstatementSteps
